import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostListener,
    afterNextRender,
    computed,
    effect,
    inject,
    signal,
    viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { s25Resource } from "../../util/s25.resource";
import { SeConnect25ThemeStore } from "./se.connect25.theme.store";
import { PermissionService } from "../../services/permission.service";
import { S25FilestoreImageComponent } from "../../standalone/s25.filestore.image.component";
import { FILE_KEY_CONNECT25_BANNER_LOGO } from "./se.connect25.constants";
import { AppPathsService } from "../../services/app.paths.service";
import { MoreMenuUtil } from "../s25-nav-bar/more-menu/more.menu.util";
import { S25RouterLinkDirective } from "../s25-nav-bar/s25.router.link.directive";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25MoreMenuItemComponent } from "../s25-nav-bar/more-menu/s25.more.menu.item.component";
import { S25ClickOutsideDirective } from "../s25-click-outside/s25.click.outside.directive";
import { MoreMenu } from "../s25-nav-bar/more-menu/more.menu";
import MenuItem = MoreMenu.MenuItem;
import { SeBreadcrumbComponent } from "./se.breadcrumb.component";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { S25CollapseChevronComponent } from "../s25-collapse-chevron/s25.collapse.chevron.component";
import { LoginStore } from "../../stores/login.store";
import { Connect25SystemAlertsCoordinatorStore } from "./c25-system-alerts/connect25.system.alerts.coordinator";
import { Connect25SystemAlertsComponent } from "./c25-system-alerts/connect25.system.alerts.component";
import { StateService } from "../../services/state.service";
import { S25States } from "../../services/state/state.map";
import { StateStore } from "../../services/state/state.store";
import { S25Const } from "../../util/s25-const";

/** Go-to-tool dropdown: tools vs switch-roles, matching the 25Live More menu sections. */
type Connect25GoToToolMenuSections = {
    toolItems: MenuItem[];
    switchRoleItems: MenuItem[];
};

@TypeManagerDecorator("s25-ng-connect25-top-banner")
@Component({
    selector: "s25-ng-connect25-top-banner",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        S25RouterLinkDirective,
        S25IconComponent,
        S25MoreMenuItemComponent,
        S25ClickOutsideDirective,
        S25FilestoreImageComponent,
        SeBreadcrumbComponent,
        S25IconButtonComponent,
        S25CollapseChevronComponent,
        Connect25SystemAlertsComponent,
    ],
    host: {
        class: "connect25-top-banner",
        "[style.--connect25-banner-bg]": "bannerBg()",
        "[style.--connect25-banner-accent]": "bannerAccent()",
        "[style.--connect25-banner-fg]": "bannerFg()",
        "[class.banner-hidden]": "!isVisible()",
    },
    template: `
        <div class="connect25-top-banner__fixed" #fixedBanner>
            <div class="connect25-top-banner__stack">
                <div class="connect25-top-banner__inner" [class.in-app]="inApp">
                    <a
                        #brandLink
                        s25-ng-router-link
                        [stateName]="'seHomeView'"
                        class="connect25-top-banner__brand"
                        [attr.aria-label]="'Connect25 dashboard'"
                    >
                        @if (hasBannerLogo()) {
                            <s25-ng-filestore-image
                                class="connect25-top-banner__logo-fs"
                                [value]="logoFileRef()"
                                [readOnly]="true"
                                [objectType]="-1"
                                [objectId]="-1"
                                [fileKey]="bannerLogoFileKey"
                            />
                        }
                        <span class="connect25-top-banner__title">{{ productTitle() }}</span>
                    </a>
                    <div class="connect25-top-banner__tools">
                        @if (showGoToTool()) {
                            <button
                                #menuTrigger
                                type="button"
                                class="connect25-top-banner__menu-btn c-button c-button--flat"
                                (click)="toggleToolMenu($event)"
                                [attr.aria-expanded]="toolMenuOpen()"
                                aria-controls="connect25-menu-panel"
                                aria-haspopup="menu"
                            >
                                <s25-ng-icon [type]="'menu'" [ariaLabel]="'Menu'" />
                                <span class="connect25-top-banner__menu-label" aria-hidden="true">Menu</span>
                            </button>
                            @if (toolMenuOpen()) {
                                <div
                                    #menuPanel
                                    id="connect25-menu-panel"
                                    class="connect25-menu"
                                    role="menu"
                                    s25-ng-click-outside
                                    (clickOutside)="closeToolMenu()"
                                    (keydown)="onMenuKeydown($event)"
                                >
                                    <div class="connect25-menu__head">
                                        <span class="connect25-menu__title">Menu</span>
                                        <s25-ng-icon-button
                                            #closeButton
                                            [type]="'close'"
                                            [size]="15"
                                            class="connect25-menu__close"
                                            [ariaLabel]="'Close menu'"
                                            [attr.tabindex]="focusedMenuIndex() === 0 ? 0 : -1"
                                            (clicked)="closeToolMenu()"
                                        />
                                    </div>
                                    @if (loginStore.loggedIn()) {
                                        <div class="connect25-menu__section">
                                            <button
                                                type="button"
                                                role="menuitem"
                                                class="connect25-menu__section-label connect25-menu__section-toggle"
                                                [attr.aria-expanded]="goToToolsExpanded()"
                                                aria-haspopup="true"
                                                [attr.tabindex]="focusedMenuIndex() === goToToolMenuIndex() ? 0 : -1"
                                                (click)="goToToolsExpanded.set(!goToToolsExpanded())"
                                            >
                                                Go to Tool
                                                <s25-ng-collapse-chevron
                                                    [collapsedOrientation]="'right'"
                                                    [expanded]="goToToolsExpanded()"
                                                />
                                            </button>
                                            @if (goToToolsExpanded()) {
                                                @for (
                                                    item of goToToolToolItems();
                                                    track item.displayName;
                                                    let toolIdx = $index
                                                ) {
                                                    <div
                                                        class="connect25-menu__row--button"
                                                        role="menuitem"
                                                        [attr.tabindex]="
                                                            focusedMenuIndex() === goToToolMenuIndex() + 1 + toolIdx
                                                                ? 0
                                                                : -1
                                                        "
                                                    >
                                                        <s25-ng-more-menu-item
                                                            [item]="item"
                                                            (itemClicked)="closeToolMenu()"
                                                        />
                                                    </div>
                                                }
                                            }
                                        </div>
                                    }
                                    <div class="connect25-menu__section">
                                        <div class="connect25-menu__section-label">Notices</div>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            class="connect25-menu__row--button"
                                            [attr.tabindex]="focusedMenuIndex() === alertsMenuIndex() ? 0 : -1"
                                            (click)="openActiveAlertsFromMenu()"
                                        >
                                            <s25-ng-icon
                                                [type]="'info'"
                                                class="connect25-menu__row-icon"
                                                [ariaLabel]="false"
                                            />
                                            <span class="connect25-menu__row-label">Active alerts</span>
                                        </button>
                                    </div>
                                    @if (goToToolSwitchRoleItems().length) {
                                        <div class="connect25-menu__section connect25-menu__switch-roles">
                                            <button
                                                type="button"
                                                role="menuitem"
                                                class="connect25-menu__section-label connect25-menu__section-toggle"
                                                [attr.aria-expanded]="switchRolesExpanded()"
                                                aria-haspopup="true"
                                                [attr.tabindex]="focusedMenuIndex() === switchRolesMenuIndex() ? 0 : -1"
                                                (click)="switchRolesExpanded.set(!switchRolesExpanded())"
                                            >
                                                Switch Roles
                                                <s25-ng-collapse-chevron
                                                    [collapsedOrientation]="'right'"
                                                    [expanded]="switchRolesExpanded()"
                                                />
                                            </button>
                                            @if (switchRolesExpanded()) {
                                                @for (
                                                    item of goToToolSwitchRoleItems();
                                                    track $index;
                                                    let roleIdx = $index
                                                ) {
                                                    <div
                                                        role="menuitem"
                                                        [attr.tabindex]="
                                                            focusedMenuIndex() === switchRolesMenuIndex() + 1 + roleIdx
                                                                ? 0
                                                                : -1
                                                        "
                                                    >
                                                        <s25-ng-more-menu-item
                                                            [item]="item"
                                                            (itemClicked)="closeToolMenu()"
                                                        />
                                                    </div>
                                                }
                                            }
                                        </div>
                                    }
                                    <div class="connect25-menu__section connect25-menu__section--account">
                                        <div class="connect25-menu__section-label">Account</div>
                                        @if (inApp && appVersion) {
                                            <div class="connect25-menu__version">Version {{ appVersion }}</div>
                                        }
                                        @if (loginStore.loggedIn()) {
                                            <button
                                                type="button"
                                                role="menuitem"
                                                class="connect25-menu__row--button connect25-menu__logout"
                                                [attr.tabindex]="focusedMenuIndex() === accountMenuIndex() ? 0 : -1"
                                                (click)="loginStore.logout('seHomeView')"
                                            >
                                                <s25-ng-icon
                                                    [type]="'signout'"
                                                    class="connect25-menu__row-icon"
                                                    [ariaLabel]="false"
                                                />
                                                <span class="connect25-menu__row-label menu-item__label"> Log Out</span>
                                            </button>
                                        } @else {
                                            <button
                                                type="button"
                                                role="menuitem"
                                                class="connect25-menu__row--button connect25-menu__logout"
                                                [attr.tabindex]="focusedMenuIndex() === accountMenuIndex() ? 0 : -1"
                                                (click)="navToLogin()"
                                            >
                                                <s25-ng-icon
                                                    [type]="'signin'"
                                                    class="connect25-menu__row-icon"
                                                    [ariaLabel]="false"
                                                />
                                                <span class="connect25-menu__row-label menu-item__label"> Log In</span>
                                            </button>
                                        }
                                    </div>
                                </div>
                            }
                        }
                    </div>
                </div>
                <se-breadcrumbs />
                <s25-ng-connect25-system-alerts (bannerEmptied)="onAlertsEmptied()" />
            </div>
        </div>
    `,
    styles: `
        :host {
            display: block;
            width: 100%;
        }

        .connect25-top-banner__fixed {
            display: contents;
        }

        .connect25-top-banner__stack {
            position: relative;
        }

        .connect25-menu {
            --cn-menu-bg: var(--color--surface-bg);
            --cn-menu-fg: var(--color--text-strong);
            --cn-menu-fg-muted: var(--color--text-muted);
            --cn-menu-fg-subtle: var(--color--text-muted);
            --cn-menu-border: var(--color--border);
            --cn-menu-hover: var(--color--surface-bg-hover);
            --cn-menu-focus-ring: color-mix(in srgb, var(--color--link-text) 25%, transparent);
            --cn-menu-danger: var(--color--danger);
            --cn-menu-danger-bg: var(--color--surface-bg-error);

            position: absolute;
            right: 0;
            top: calc(100% + 8px);
            z-index: 21;
            min-width: 260px;
            max-width: 320px;
            padding: 6px 0;
            background: var(--cn-menu-bg);
            color: var(--cn-menu-fg);
            border: 1px solid var(--cn-menu-border);
            border-radius: 10px;
            box-shadow: var(--shadow--dropdown);
        }

        .connect25-menu__head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 14px 6px;
        }
        .connect25-menu__title {
            font-weight: 600;
            letter-spacing: 0.01em;
            color: var(--cn-menu-fg);
        }
        .connect25-menu__close,
        :host ::ng-deep .connect25-menu__close svg {
            color: var(--cn-menu-fg-muted);
        }
        .connect25-menu__close:hover {
            color: var(--cn-menu-fg);
        }

        .connect25-menu__section {
            padding: 4px 0 6px;
        }
        .connect25-menu__section + .connect25-menu__section {
            border-top: 1px solid var(--cn-menu-border);
            margin-top: 4px;
            padding-top: 8px;
        }
        .connect25-menu__section-label {
            padding: 6px 14px 4px;
            font-size: 11px;
            font-weight: 600;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: var(--cn-menu-fg-subtle);
        }

        .connect25-menu__section-label s25-ng-collapse-chevron {
            float: right;
        }

        .connect25-menu__section-toggle {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            background: transparent;
            border: 0;
            cursor: pointer;
        }

        .connect25-menu__section-toggle:hover {
            background: var(--cn-menu-hover);
        }

        .connect25-menu__section-toggle:focus-visible {
            outline: none;
            background: var(--cn-menu-hover);
            box-shadow: inset 0 0 0 2px var(--cn-menu-focus-ring);
        }

        .connect25-menu__row--button,
        :host ::ng-deep .connect25-menu__switch-roles s25-ng-more-menu-item s25-ng-masquerade-switch-roles div > div {
            width: 100%;
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 5px 14px;
            background: transparent;
            border: 0;
            text-align: left;
            font: inherit;
            font-size: 13.5px;
            color: var(--cn-menu-fg);
            cursor: pointer;
            transition:
                background-color var(--transition-short) ease,
                color var(--transition-short) ease;
        }

        .connect25-menu__row--button:hover {
            background: var(--cn-menu-hover);
        }

        .connect25-menu__row--button:focus-visible,
        [role="menuitem"]:focus-visible {
            outline: none;
            background: var(--cn-menu-hover);
            box-shadow: inset 0 0 0 2px var(--cn-menu-focus-ring);
        }

        .connect25-menu__row-icon {
            color: var(--cn-menu-fg-muted);
            flex-shrink: 0;
        }
        .connect25-menu__row-label {
            flex: 1;
            min-width: 0;
        }

        .connect25-menu__logout:hover,
        .connect25-menu__logout:focus-visible {
            background: var(--cn-menu-danger-bg);
            color: var(--cn-menu-danger);
        }
        .connect25-menu__logout:hover .connect25-menu__row-icon,
        .connect25-menu__logout:focus-visible .connect25-menu__row-icon {
            color: var(--cn-menu-danger);
        }

        .connect25-menu__section--account {
            padding-bottom: 4px;
        }

        .connect25-menu__version {
            padding: 6px 14px;
            font-size: 12px;
            color: var(--cn-menu-fg-muted);
        }

        .connect25-top-banner__inner {
            background: var(--connect25-banner-bg, #46587c);
            color: var(--connect25-banner-fg, #fff);
            border-bottom: 2px solid var(--connect25-banner-accent, #04345d);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 1.5rem;
            padding: 0.875rem 1.25rem;
        }
        .connect25-top-banner__brand {
            display: flex;
            align-items: center;
            gap: 1rem;
            min-width: 0;
            color: inherit;
            text-decoration: none;
            outline: none;
        }
        .connect25-top-banner__brand:hover,
        .connect25-top-banner__brand:focus {
            text-decoration: none;
            outline: none;
            opacity: 0.9;
        }
        .connect25-top-banner__brand:focus-visible {
            outline: none;
            box-shadow: var(--shadow--inner-focus);
        }
        .connect25-top-banner__logo-fs {
            flex-shrink: 0;
            line-height: 0;
        }
        :host .connect25-top-banner__logo-fs ::ng-deep .fs-image__preview {
            display: block;
            max-height: 5rem;
            width: auto;
            max-width: 180px;
            object-fit: contain;
            border: none;
        }
        .connect25-top-banner__title {
            font-weight: 600;
            font-size: 1.25rem;
            letter-spacing: -0.01em;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            opacity: 0.95;
        }
        .connect25-top-banner__tools {
            position: relative;
            flex-shrink: 0;
            display: flex;
            align-items: center;
        }
        .connect25-top-banner__menu-btn {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 0.75rem;
            border-radius: 0.25rem;
            color: inherit !important;
            background: transparent;
            border: 1px solid color-mix(in srgb, var(--connect25-banner-fg, #fff) 20%, transparent);
            transition:
                background var(--transition-short) ease,
                border-color var(--transition-short) ease;
        }
        .connect25-top-banner__menu-btn:hover {
            background: color-mix(in srgb, var(--connect25-banner-fg, #fff) 10%, transparent);
            border-color: color-mix(in srgb, var(--connect25-banner-fg, #fff) 30%, transparent);
        }
        .connect25-top-banner__menu-label {
            font-size: 0.875rem;
            font-weight: 500;
        }

        @media (max-width: 930px) {
            .connect25-top-banner__fixed {
                display: block;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                z-index: 100;
                transform: translateY(0);
                transition: transform var(--transition-medium) ease;
            }

            :host.banner-hidden .connect25-top-banner__fixed {
                transform: translateY(-100%);
            }

            :host.banner-hidden:focus-within .connect25-top-banner__fixed {
                transform: translateY(0);
            }

            .connect25-top-banner__inner {
                padding: 0.625rem 1rem;
            }

            .connect25-top-banner__inner.in-app {
                padding: env(safe-area-inset-top) 1rem 0.625rem;
            }

            .connect25-top-banner__title {
                font-size: 1rem;
            }

            .connect25-top-banner__menu-btn {
                padding: 0.5rem;
                border: none;
            }

            .connect25-top-banner__menu-btn:hover {
                border: none;
            }

            .connect25-top-banner__menu-label {
                display: none;
            }
        }
    `,
})
export class Connect25TopBannerComponent {
    private static readonly MOBILE_BREAKPOINT = 930;

    readonly bannerLogoFileKey = FILE_KEY_CONNECT25_BANNER_LOGO;

    private readonly themeStore = inject(SeConnect25ThemeStore);

    private readonly access = s25Resource({
        loader: () => PermissionService.has25LiveAccess(),
    });

    private readonly goToTool = s25Resource({
        params: () => this.access.value(),
        loader: async ({ params: hasAccess }): Promise<Connect25GoToToolMenuSections> => {
            if (!hasAccess) {
                return { toolItems: [], switchRoleItems: [] };
            }
            const [toolItems, switchRoleItems] = await Promise.all([
                AppPathsService.getGoToToolMenuItems(),
                MoreMenuUtil.getSwitchRolesMenuItems(),
            ]);
            return { toolItems, switchRoleItems };
        },
    });

    readonly loginStore = inject(LoginStore);

    private readonly systemAlertsCoordinator = inject(Connect25SystemAlertsCoordinatorStore);

    readonly logoFileRef = computed(() => this.themeStore.logoFileRef().trim());

    readonly hasBannerLogo = computed(() => !!this.logoFileRef());

    readonly productTitle = computed(() => this.themeStore.productTitle());

    readonly bannerBg = computed(() => this.themeStore.bannerBackground());
    readonly bannerAccent = computed(() => this.themeStore.bannerAccent());
    readonly bannerFg = computed(() => this.themeStore.bannerForeground());

    readonly showGoToTool = computed(() => !!this.access.value());

    readonly goToToolToolItems = computed(() => this.goToTool.value()?.toolItems ?? []);

    readonly goToToolSwitchRoleItems = computed(() => this.goToTool.value()?.switchRoleItems ?? []);

    readonly toolMenuOpen = signal(false);

    goToToolsExpanded = signal(false);
    switchRolesExpanded = signal(false);

    private readonly fixedBanner = viewChild<ElementRef<HTMLElement>>("fixedBanner");
    private readonly brandLink = viewChild<ElementRef<HTMLAnchorElement>>("brandLink");
    private readonly menuTrigger = viewChild<ElementRef<HTMLButtonElement>>("menuTrigger");
    private readonly menuPanel = viewChild<ElementRef<HTMLElement>>("menuPanel");

    readonly focusedMenuIndex = signal(0);

    private readonly scrollY = signal(0);
    private readonly lastScrollY = signal(0);
    private readonly scrollDirection = signal<"up" | "down">("up");
    private readonly isMobile = signal(window.innerWidth <= Connect25TopBannerComponent.MOBILE_BREAKPOINT);
    protected readonly inApp = S25Const.inApp;
    protected readonly appVersion = S25Const.appVersion;
    private ignoreScrollUntil = 0;

    readonly isAtTop = computed(() => this.scrollY() < 5);

    readonly isVisible = computed(() => {
        if (!this.isMobile()) return true;
        return this.isAtTop() || this.scrollDirection() === "up";
    });

    private readonly visibleMenuItems = computed(() => {
        const items: string[] = ["close"];

        if (this.loginStore.loggedIn()) {
            items.push("goToTool");
            if (this.goToToolsExpanded()) {
                this.goToToolToolItems().forEach((_, idx) => items.push(`tool-${idx}`));
            }
        }

        items.push("alerts");

        if (this.goToToolSwitchRoleItems().length) {
            items.push("switchRoles");
            if (this.switchRolesExpanded()) {
                this.goToToolSwitchRoleItems().forEach((_, idx) => items.push(`role-${idx}`));
            }
        }

        items.push("account");

        return items;
    });

    readonly closeButtonIndex = 0;
    readonly goToToolMenuIndex = computed(() => this.visibleMenuItems().indexOf("goToTool"));
    readonly alertsMenuIndex = computed(() => this.visibleMenuItems().indexOf("alerts"));
    readonly switchRolesMenuIndex = computed(() => this.visibleMenuItems().indexOf("switchRoles"));
    readonly accountMenuIndex = computed(() => this.visibleMenuItems().indexOf("account"));
    readonly totalMenuItems = computed(() => this.visibleMenuItems().length);

    private resizeObserver?: ResizeObserver;
    private readonly stateStore = inject(StateStore);

    constructor() {
        void MoreMenuUtil.applyDarkModeFromUserPreference();

        // we're giving a little flexibility to allow for slight logo size variation, so calculate banner height in mobile
        afterNextRender(() => {
            // Ignore scroll events briefly after render to avoid false triggers during page load
            this.ignoreScrollUntil = Date.now() + 300;
            if (this.isMobile()) {
                this.setupResizeObserver();
                this.measureAndSetBannerHeight();
                // Reset scroll position after grace period to fix any drift from initial layout
                setTimeout(() => window.scrollTo(0, 0), 300);
            }
        });

        // Scroll to top on route changes within Connect25 (mobile only)
        effect(() => {
            this.stateStore.currentStateName(); // Track state changes
            if (this.isMobile()) {
                window.scrollTo(0, 0);
            }
        });
    }

    private setupResizeObserver() {
        const banner = this.fixedBanner()?.nativeElement;
        if (banner && typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(() => {
                this.measureAndSetBannerHeight();
            });
            this.resizeObserver.observe(banner);
        }
    }

    @HostListener("window:scroll")
    onWindowScroll() {
        if (!this.isMobile()) return;
        if (Date.now() < this.ignoreScrollUntil) return;
        this.updateScrollState();
    }

    @HostListener("window:resize")
    onWindowResize() {
        const wasMobile = this.isMobile();
        this.isMobile.set(window.innerWidth <= Connect25TopBannerComponent.MOBILE_BREAKPOINT);

        if (this.isMobile() && !wasMobile) {
            this.setupResizeObserver();
            this.measureAndSetBannerHeight();
        } else if (!this.isMobile() && wasMobile) {
            this.resizeObserver?.disconnect();
            this.resizeObserver = undefined;
        }
    }

    private measureAndSetBannerHeight() {
        if (!this.isMobile()) return;
        const banner = this.fixedBanner()?.nativeElement;
        if (banner) {
            const height = banner.offsetHeight;
            if (height > 0) {
                document.documentElement.style.setProperty("--connect25-banner-height", `${height}px`);
            }
        }
    }

    private updateScrollState() {
        const currentScrollY = window.scrollY || document.documentElement.scrollTop || 0;
        this.scrollY.set(currentScrollY);

        if (Math.abs(currentScrollY - this.lastScrollY()) > 3) {
            this.scrollDirection.set(currentScrollY > this.lastScrollY() ? "down" : "up");
            this.lastScrollY.set(currentScrollY);
        }
    }

    toggleToolMenu(ev?: Event) {
        ev?.stopPropagation();
        const wasOpen = this.toolMenuOpen();
        this.toolMenuOpen.update((open: boolean) => !open);
        if (!wasOpen) {
            this.focusedMenuIndex.set(0);
            setTimeout(() => this.focusCurrentMenuItem());
        }
    }

    closeToolMenu() {
        this.toolMenuOpen.set(false);
        this.menuTrigger()?.nativeElement.focus();
    }

    /** The last banner alert was acknowledged and its strip is unmounting, so focus must land on something that persists. */
    onAlertsEmptied(): void {
        const persistentControl = this.menuTrigger()?.nativeElement ?? this.brandLink()?.nativeElement;
        persistentControl?.focus();
    }

    @HostListener("document:keydown.escape")
    onEscape() {
        if (this.toolMenuOpen()) this.closeToolMenu();
    }

    onMenuKeydown(event: KeyboardEvent) {
        const total = this.totalMenuItems();
        const currentIndex = this.focusedMenuIndex();

        switch (event.key) {
            case "ArrowDown":
            case "Tab":
                if (event.key === "Tab" && event.shiftKey) {
                    event.preventDefault();
                    this.focusedMenuIndex.set((currentIndex - 1 + total) % total);
                } else {
                    event.preventDefault();
                    this.focusedMenuIndex.set((currentIndex + 1) % total);
                }
                this.focusCurrentMenuItem();
                break;
            case "ArrowUp":
                event.preventDefault();
                this.focusedMenuIndex.set((currentIndex - 1 + total) % total);
                this.focusCurrentMenuItem();
                break;
            case "Home":
                event.preventDefault();
                this.focusedMenuIndex.set(0);
                this.focusCurrentMenuItem();
                break;
            case "End":
                event.preventDefault();
                this.focusedMenuIndex.set(total - 1);
                this.focusCurrentMenuItem();
                break;
        }
    }

    private focusCurrentMenuItem() {
        const panel = this.menuPanel()?.nativeElement;
        if (!panel) return;
        const index = this.focusedMenuIndex();
        if (index === this.closeButtonIndex) {
            const closeBtn = panel.querySelector<HTMLElement>(".connect25-menu__close button");
            closeBtn?.focus();
        } else {
            const items = panel.querySelectorAll<HTMLElement>("[role='menuitem']");
            const targetItem = items[index - 1];
            targetItem?.focus();
        }
    }

    navToLogin() {
        StateService.go(S25States.names.seLogin, null, null);
    }

    openActiveAlertsFromMenu(): void {
        this.systemAlertsCoordinator.requestOpenAllAlerts();
        this.closeToolMenu();
    }
}
